import { Card, Grid, Button, Checkbox, FormControlLabel } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { instanceOf } from "prop-types";
import { useEffect, useState } from "react";

import { ImageDropzone } from "0.Mine/0.Components/Reusable/ImageDropzone";
import { UseHandleBusiness } from "0.Mine/1.Hooks/business/business";
import { useHandleImageUpload } from "0.Mine/1.Hooks/business/images";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import ArgonBox from "components/ArgonBox";
import ArgonDropzone from "components/ArgonDropzone";
import ArgonSelect from "components/ArgonSelect";
import ArgonSnackbar from "components/ArgonSnackbar";
import ArgonTagInput from "components/ArgonTagInput";
import ArgonTypography from "components/ArgonTypography";
import FormField from "layouts/pages/account/components/FormField";

const categoryOptions = [
  { value: "retail", label: "Retail" },
  { value: "restaurant", label: "Restaurant" },
  { value: "technology", label: "Technology" },
  { value: "healthcare", label: "Healthcare" },
  { value: "education", label: "Education" },
];

const prefixOptions = [
  { value: "+1", label: "+1 (US/Canada)" },
  { value: "+44", label: "+44 (UK)" },
  { value: "+61", label: "+61 (Australia)" },
  { value: "+33", label: "+33 (France)" },
];

const yearOptions = Array.from({ length: 50 }, (_, i) => ({
  value: 2025 - i,
  label: String(2025 - i),
}));

function BusinessInfo() {
  const [tags, setTags] = useState([]);
  const [businessData, setBusinessData] = useState({
    name: "",
    category: "",
    phoneNumberPrefix: "+1",
    address: "",
    postalCode: "",
    province: "",
    country: "",
    city: "",
    phoneNumber: "",
    about: "",
    email: "",
    websiteUrl: "",
    ownerFirstName: "",
    ownerLastName: "",
    businessTagLine: "",
    yearOfIncorporation: "",
    facebookUrl: "",
    instagramUrl: "",
    linkedinUrl: "",
    twitterUrl: "",
    onqlickplace: false,
  });
  const { session, updateBusiness } = useBusinessContext();
  const { editBusiness } = UseHandleBusiness();
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const { uploadImage } = useHandleImageUpload();
  const [show, setShow] = useState(false);
  const toggleSnackbar = () => setShow(!show);

  // Set form fields once session.selectedBusiness is available.
  useEffect(() => {
    if (session && session.selectedBusiness) {
      setBusinessData({
        name: session.selectedBusiness.name ?? "",
        category: session.selectedBusiness.category ?? "",
        phoneNumberPrefix: session.selectedBusiness.phoneNumberPrefix ?? "+1",
        address: session.selectedBusiness.address ?? "",
        postalCode: session.selectedBusiness.postalCode ?? "",
        province: session.selectedBusiness.province ?? "",
        country: session.selectedBusiness.country ?? "",
        city: session.selectedBusiness.city ?? "",
        phoneNumber: session.selectedBusiness.phoneNumber || "",
        about: session.selectedBusiness.about ?? "",
        email: session.selectedBusiness.email ?? "",
        websiteUrl: session.selectedBusiness.websiteUrl ?? "",
        ownerFirstName: session.selectedBusiness.ownerFirstName ?? "",
        ownerLastName: session.selectedBusiness.ownerLastName ?? "",
        businessTagLine: session.selectedBusiness.businessTagLine ?? "",
        yearOfIncorporation: session.selectedBusiness.yearOfIncorporation
          ? session.selectedBusiness.yearOfIncorporation.toString()
          : "",
        facebookUrl: session.selectedBusiness.facebookUrl ?? "",
        instagramUrl: session.selectedBusiness.instagramUrl ?? "",
        linkedinUrl: session.selectedBusiness.linkedinUrl ?? "",
        twitterUrl: session.selectedBusiness.twitterUrl ?? "",
        onqlickplace: session.selectedBusiness.onqlickplace ?? false,
      });
    }
  }, [session]);

  if (!session) {
    return <p>Please select a business</p>;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessData({
      ...businessData,
      [name]: value,
    });
  };

  const handleSelectChange = (name, value) => {
    setBusinessData({
      ...businessData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setBusinessData({
      ...businessData,
      [name]: checked,
    });
  };

  // Updated handlers that accept the file directly from ImageDropzone
  const handleFileChangeLogo = async (fileData) => {
    console.log("Logo file data:", fileData);
    // Check if we received a File object directly
    if (fileData instanceof File) {
      setLogoFile(fileData);
      const logourl = await uploadImage(fileData, "logo", session.selectedBusiness.id);
      setBusinessData({
        ...businessData,
        logoUrl: logourl,
      });
    }
    // Check if we received an event object (from a traditional file input)
    else if (fileData && fileData.target && fileData.target.files && fileData.target.files[0]) {
      const file = fileData.target.files[0];
      setLogoFile(file);
      const logourl = await uploadImage(file, "logo", session.selectedBusiness.id);
      setBusinessData({
        ...businessData,
        logoUrl: logourl,
      });
    }
    // Check if we received a path-based object
    else if (fileData && (fileData.path || fileData.file)) {
      const file = fileData.file || fileData;
      setLogoFile(file);
      const logourl = await uploadImage(file, "logo", session.selectedBusiness.id);
      setBusinessData({
        ...businessData,
        logoUrl: logourl,
      });
    } else {
      console.error("Unexpected file format received:", fileData);
    }
  };

  const handleFileChangeCover = async (fileData) => {
    console.log("Cover file data:", fileData);
    // Check if we received a File object directly
    if (fileData instanceof File) {
      setCoverFile(fileData);
      const coverUrl = await uploadImage(fileData, "cover", session.selectedBusiness.id);
      setBusinessData({
        ...businessData,
        coverUrl: coverUrl,
      });
    }
    // Check if we received an event object (from a traditional file input)
    else if (fileData && fileData.target && fileData.target.files && fileData.target.files[0]) {
      const file = fileData.target.files[0];
      setCoverFile(file);
      const coverUrl = await uploadImage(file, "cover", session.selectedBusiness.id);
      setBusinessData({
        ...businessData,
        coverUrl: coverUrl,
      });
    }
    // Check if we received a path-based object
    else if (fileData && (fileData.path || fileData.file)) {
      const file = fileData.file || fileData;
      setCoverFile(file);
      const coverUrl = await uploadImage(file, "cover", session.selectedBusiness.id);
      setBusinessData({
        ...businessData,
        coverUrl: coverUrl,
      });
    } else {
      console.error("Unexpected file format received:", fileData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Build the Business object based on the current state.
    const business = {
      id: session.selectedBusiness.id,
      userId: session.selectedBusiness.userId,
      name: businessData.name,
      category: businessData.category.value || "category not selected",
      phoneNumberPrefix: businessData.phoneNumberPrefix,
      address: businessData.address,
      postalCode: businessData.postalCode,
      province: businessData.province,
      country: businessData.country,
      city: businessData.city,
      phoneNumber: Number(businessData.phoneNumber),
      websiteUrl: businessData.websiteUrl,
      logoUrl: businessData.logoUrl || session.selectedBusiness.logoUrl,
      coverUrl: businessData.coverUrl || session.selectedBusiness.coverUrl,

      about: businessData.about,
      ownerFirstName: businessData.ownerFirstName,
      ownerLastName: businessData.ownerLastName,
      businessTagLine: businessData.businessTagLine,
      yearOfIncorporation: businessData.yearOfIncorporation
        ? Number(businessData.yearOfIncorporation)
        : undefined,
      facebookUrl: businessData.facebookUrl,
      instagramUrl: businessData.instagramUrl,
      linkedinUrl: businessData.linkedinUrl,
      twitterUrl: businessData.twitterUrl,
      businessHours: session.selectedBusiness.businessHours,
      connectedSocials: session.selectedBusiness.connectedSocials,
      email: businessData.email,
      isFranchise: false,
      locationHidden: false,
      onqlickplace: businessData.onqlickplace,
    };
    console.log("Business Object:", business);
    try {
      await updateBusiness(business);
      const api = await editBusiness(business);
      if (api) {
        enqueueSnackbar("Business aggiornato con successo!", {
          variant: "success",
        });
      } else {
        console.log("API FAILED!");
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Card
      id="business-info"
      maxWidth="100%"
      sx={{ overflow: "visible", maxWidth: "100%", boxShadow: "unset!important" }}
    >
      <ArgonBox p={3}>
        <ArgonTypography variant="h5">Informazioni aziendali</ArgonTypography>
      </ArgonBox>
      <ArgonBox component="form" pb={3} px={3} onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Form fields using businessData as controlled values */}
          <Grid item xs={12} sm={6}>
            <FormField
              label="Nome azienda"
              placeholder="Inserisci il nome dell'azienda"
              name="name"
              value={businessData.name}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Categoria di business
              </ArgonTypography>
            </ArgonBox>
            <ArgonSelect
              placeholder="Seleziona categoria"
              options={categoryOptions}
              value={businessData.category}
              onChange={(value) => handleSelectChange("category", value)}
            />
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6}>
            <FormField
              label="Email"
              placeholder="business@example.com"
              inputProps={{ type: "email" }}
              name="email"
              value={businessData.email}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Prefisso
              </ArgonTypography>
            </ArgonBox>
            <ArgonSelect
              placeholder="+1"
              options={prefixOptions}
              onChange={(value) => handleSelectChange("phoneNumberPrefix", value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormField
              label="Numero di telefono"
              placeholder="1234567890"
              inputProps={{ type: "number" }}
              name="phoneNumber"
              value={businessData.phoneNumber || ""}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Location Info */}
          <Grid item xs={12} sm={6}>
            <FormField
              label="Indirizzo"
              placeholder="123 Business St"
              name="address"
              value={businessData.address}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Città"
              placeholder="City name"
              name="city"
              value={businessData.city}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormField
              label="Provincia/Stato"
              placeholder="Province/State"
              name="province"
              value={businessData.province}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormField
              label="Codice Postale/Zip Code"
              placeholder="Postal code"
              name="postalCode"
              value={businessData.postalCode}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormField
              label="Paese"
              placeholder="Paese"
              name="country"
              value={businessData.country}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Online Presence */}
          <Grid item xs={12} sm={6}>
            <FormField
              label="Website"
              placeholder="https://www.business.com"
              name="websiteUrl"
              value={businessData.websiteUrl || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Slogan aziendale"
              placeholder="Slogan"
              name="businessTagLine"
              value={businessData.businessTagLine || ""}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Social Media */}
          <Grid item xs={12}>
            <ArgonTypography variant="h6" mb={2}>
              Social Media
            </ArgonTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Facebook URL"
              placeholder="https://facebook.com/yourbusiness"
              name="facebookUrl"
              value={businessData.facebookUrl || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Instagram URL"
              placeholder="https://instagram.com/yourbusiness"
              name="instagramUrl"
              value={businessData.instagramUrl || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="LinkedIn URL"
              placeholder="https://linkedin.com/company/yourbusiness"
              name="linkedinUrl"
              value={businessData.linkedinUrl || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Twitter URL"
              placeholder="https://twitter.com/yourbusiness"
              name="twitterUrl"
              value={businessData.twitterUrl || ""}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Owner Details */}
          <Grid item xs={12}>
            <ArgonTypography variant="h6" mb={2}>
              Informazioni Titolare Azienda
            </ArgonTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Nome del responsabile"
              placeholder="First Name"
              name="ownerFirstName"
              value={businessData.ownerFirstName || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              label="Cognome del responsabile"
              placeholder="Last Name"
              name="ownerLastName"
              value={businessData.ownerLastName || ""}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Anno di costituzione
              </ArgonTypography>
            </ArgonBox>
            <ArgonSelect
              placeholder="Seleziona data"
              options={yearOptions}
              onChange={(value) => handleSelectChange("yearOfIncorporation", value)}
            />
          </Grid>

          <Grid item xs={12}>
            <ArgonTypography variant="h6" mb={2}>
              Business Media
            </ArgonTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Business Logo
              </ArgonTypography>
            </ArgonBox>
            <ImageDropzone
              onDropFile={handleFileChangeLogo}
              initialImage={session.selectedBusiness?.logoUrl}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Immagine di copertina
              </ArgonTypography>
            </ArgonBox>
            <ImageDropzone
              onDropFile={handleFileChangeCover}
              initialImage={session.selectedBusiness?.coverUrl}
            />
          </Grid>

          {/* About */}
          <Grid item xs={12}>
            <FormField
              label="Informazioni sull'attività"
              placeholder="Raccontaci qualcosa..."
              name="about"
              value={businessData.about}
              onChange={handleInputChange}
              multiline
              rows={5}
            />
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <ArgonTypography component="label" variant="caption" fontWeight="bold">
                Business Tags
              </ArgonTypography>
            </ArgonBox>
            <ArgonTagInput
              tags={tags}
              placeholder="Aggiungi tag aziendali"
              onChange={(newTags) => setTags(newTags)}
              removeOnBackspace
            />
          </Grid>

          {/* OnQlickPlace Checkbox */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={businessData.onqlickplace}
                  onChange={handleCheckboxChange}
                  name="onqlickplace"
                  color="primary"
                />
              }
              label={
                <ArgonTypography variant="button" fontWeight="regular" color="text">
                  Visualizza su QlickPlace
                </ArgonTypography>
              }
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button type="submit" variant="contained" color="primary">
              Salva le informazioni aziendali
            </Button>
          </Grid>
        </Grid>
      </ArgonBox>
      <ArgonSnackbar
        color="info"
        icon="notifications"
        title="Business Updated successfully"
        content="Your content is now live!"
        open={show}
        close={toggleSnackbar}
      />
    </Card>
  );
}

export default BusinessInfo;
